import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Customer } from '../../types';
import { getCustomerType } from '../../customerHelpers';
import { InfoItemDd, InfoItemDt, InfoItemList, Section, SectionHeader } from '../../common';
import CustomerInput from './CustomerInput';
import { DateTime } from '../../../components/Formatters';
import { validateEmail, validatePhoneNumber } from '../../../helpers/validation';
import { formatDate } from '../../../helpers/formatters';
import FavoriteStore from './FavoriteStore';

interface Props extends WithTranslation {
    customer: Customer;
    accountId: string;
}

const BasicInfo = ({ customer, t, i18n, accountId }: Props) => (
    <Section>
        <SectionHeader>Info</SectionHeader>
        <InfoItemList>
            <InfoItemDt>ID</InfoItemDt>
            <InfoItemDd>{customer.id}</InfoItemDd>
        </InfoItemList>
        <InfoItemList>
            <InfoItemDt>{t('customers.fields.created_at')}</InfoItemDt>
            <InfoItemDd>
                <DateTime isoString={customer.created_at || ''} boldTime />
            </InfoItemDd>
        </InfoItemList>
        <InfoItemList>
            <InfoItemDt>{t('customers.fields.updated_at')}</InfoItemDt>
            <InfoItemDd>
                <DateTime isoString={customer.updated_at || ''} boldTime />
            </InfoItemDd>
        </InfoItemList>
        {customer.deleted_at && (
            <InfoItemList>
                <InfoItemDt>{t('customers.fields.deleted_at')}</InfoItemDt>
                <InfoItemDd>
                    <DateTime isoString={customer.deleted_at || ''} boldTime />
                </InfoItemDd>
            </InfoItemList>
        )}
        <InfoItemList>
            <InfoItemDt>{t('customers.fields.customer_id')}</InfoItemDt>
            <InfoItemDd>{customer.customer_id}</InfoItemDd>
        </InfoItemList>
        <CustomerInput
            formElement="input"
            accountId={accountId}
            customer={customer}
            label={t('customers.fields.first_name')}
            name="first_name"
            type="text"
            value={customer.first_name}
            notSetValue="N/A"
        />

        <CustomerInput
            formElement="input"
            accountId={accountId}
            customer={customer}
            label={t('customers.fields.last_name')}
            name="last_name"
            type="text"
            value={customer.last_name}
            notSetValue="N/A"
        />

        <CustomerInput
            formElement="input"
            accountId={accountId}
            customer={customer}
            label={t('customers.fields.email')}
            validationFunction={validateEmail}
            validationText={t('customers.fields.form.invalid_email')}
            name="email"
            type="text"
            value={customer.email}
            notSetValue="N/A"
        />

        <CustomerInput
            formElement="dropdown"
            accountId={accountId}
            customer={customer}
            label={t('customers.fields.gender')}
            name="gender"
            value={customer.gender}
            displayValue={t(`customers.fields.genders.${customer.gender}`, { defaultValue: customer.gender || 'N/A' })}
            options={[{ male: t('customers.fields.genders.male') }, { female: t('customers.fields.genders.female') }]}
            notSetValue="N/A"
        />

        {/* FIXME: If the user clears this input and saves, the api responds with a 400 Bad Request */}
        <CustomerInput
            formElement="input"
            accountId={accountId}
            customer={customer}
            label={t('customers.fields.date_of_birth')}
            name="date_of_birth"
            type="date"
            value={customer.date_of_birth}
            notSetValue="N/A"
            displayValue={customer.date_of_birth ? formatDate(customer.date_of_birth, i18n.language) : ''}
        />

        <CustomerInput
            formElement="input"
            accountId={accountId}
            customer={customer}
            label={t('customers.fields.phone')}
            validationFunction={validatePhoneNumber}
            validationText={t('customers.fields.form.invalid_phone')}
            name="phone_number"
            type="text"
            value={customer.phone_number}
            notSetValue="N/A"
        />

        <InfoItemList>
            <InfoItemDt>Type</InfoItemDt>
            <InfoItemDd>{t(getCustomerType(customer))}</InfoItemDd>
        </InfoItemList>

        <InfoItemList>
            <InfoItemDt>{t('customers.fields.enrolled_by')}</InfoItemDt>
            <InfoItemDd>{customer.enrolled_by?.value || 'N/A'}</InfoItemDd>
        </InfoItemList>
        {customer.created_at && (
            <InfoItemList>
                <InfoItemDt>{t('customers.fields.created_at')}</InfoItemDt>
                <InfoItemDd>{new Date(customer.created_at).toLocaleString()}</InfoItemDd>
            </InfoItemList>
        )}
        <FavoriteStore accountId={accountId} customer={customer} />
    </Section>
);

export default withTranslation()(BasicInfo);
